import { Heading, TooltipHeading } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import {
  GreetingDialogDisplayCaptions,
  GreetingDialogDisplayList,
  PromoterConversationContinueBehaviourConditionOperator,
  PromoterConversationContinueBehaviourConditionOperatorCaptions,
  PromoterModel,
} from '@mm/state/model/PromoterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import NumberInput from '../../../../../../common/components/NumberInput/NumberInput';
import { MappedTypedPrimarySelect } from '../../../../../../common/components/PrimarySelect/TypedPrimarySelect';
import { RequiredPlanBadge } from '../../../../../../common/components/ProBadge';
import RadioInput from '../../../../../../common/components/RadioInput';
import SelectorWithArrow, {
  itemsFromObject,
} from '../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import TextInput from '../../../../../../common/components/TextInput';
import { CustomerChatEditorAccordionItem } from '../CustomerChatEditorAccordionItem/CustomerChatEditorAccordionItem';
import './CustomerChatGreetingBehaviourEditor.pcss';

const getInitialLocalPromoter = (promoter: PromoterModel) => {
  return {
    ...promoter,
    greetingDialogDisplay: promoter.greetingDialogDisplay || 'show',
    greetingDialogDisplayMobile: promoter.greetingDialogDisplayMobile || 'show',
    conversationContinueBehaviour: promoter.conversationContinueBehaviour || {
      mode: 'resume',
    },
  };
};

type Props = {
  promoter: PromoterModel;
  usedInSlideout?: boolean;
};

export const CustomerChatGreetingBehaviourEditor = (props: Props) => {
  const { promoter, usedInSlideout } = props;
  const { id: botId } = useSelector(activeChatbotSelector);

  const promoterCRUDActions = usePromoterCRUDActions();
  const { updateSetupStepsRemainingByPageIdUpdated } = useChatbotCRUDActions();
  const featureFlags = useSelector(combinedFeatureFlagsSelector);

  const [localPromoter, setLocalPromoter] = useState(
    getInitialLocalPromoter(promoter),
  );
  const [
    conversationContinueBehaviourConditionValue,
    setConversationContinueBehaviourConditionValue,
  ] = useState(
    localPromoter.conversationContinueBehaviour.condition?.value || '',
  );
  const canEditGreetingBehaviour = useSelector(
    combinedFeatureFlagsSelector,
  ).customerChatEditGreetingBehaviour;
  const modalActions = useModalActions();

  // Update localPromoter whenever the original object changes
  useEffect(() => {
    setLocalPromoter(getInitialLocalPromoter(promoter));
  }, [promoter]);

  const updatePromoter = (updatedPromoter: PromoterModel) => {
    updateSetupStepsRemainingByPageIdUpdated(updatedPromoter.pageId);
    return promoterCRUDActions.update(botId, updatedPromoter);
  };

  const updatePromoterFields = (updatedFields: Partial<PromoterModel>) => {
    updatePromoter({ ...localPromoter, ...updatedFields });
  };

  const armedLandmine = (landmineName: string, operation: Function) => {
    if (!canEditGreetingBehaviour) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName,
        feature: 'customerChatEditGreetingBehaviour',
      });
      return;
    }
    operation();
  };

  return (
    <CustomerChatEditorAccordionItem
      title={
        <div
          className={
            usedInSlideout
              ? 'customer-chat-greeting-behaviour-editor__heading--slideout'
              : 'customer-chat-greeting-behaviour-editor__heading'
          }
        >
          <Heading
            size={usedInSlideout ? 5 : 4}
            text="Greeting Behaviour"
            isBold={!usedInSlideout}
          />
          <RequiredPlanBadge billingType={PageBillingType.Pro} />
        </div>
      }
      facebook={!usedInSlideout}
      webchat={!usedInSlideout}
      descriptionText="Display &amp; Delay Settings"
      usedInSlideout={usedInSlideout}
    >
      <Heading size={4} addMarginAbove={!usedInSlideout}>
        Greeting Delay
      </Heading>
      <div
        className={
          usedInSlideout
            ? 'customer-chat-greeting-behaviour-editor__behaviour-grid--slideout'
            : 'customer-chat-greeting-behaviour-editor__behaviour-grid'
        }
      >
        <div>Desktop</div>
        <MappedTypedPrimarySelect
          activeItemClassName={
            usedInSlideout
              ? 'customer-chat-greeting-behaviour-editor__behaviour-grid__primary-select'
              : ''
          }
          captions={GreetingDialogDisplayCaptions}
          list={GreetingDialogDisplayList}
          value={localPromoter.greetingDialogDisplay}
          onChange={k =>
            armedLandmine(
              landmineNames[
                'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__EDIT-CUSTOMER-CHAT__DESKTOP-GREETING-CHANGE'
              ],
              () => updatePromoterFields({ greetingDialogDisplay: k.id }),
            )
          }
        />

        <div>Mobile</div>
        <MappedTypedPrimarySelect
          activeItemClassName={
            usedInSlideout
              ? 'customer-chat-greeting-behaviour-editor__behaviour-grid__primary-select'
              : ''
          }
          captions={GreetingDialogDisplayCaptions}
          list={GreetingDialogDisplayList}
          value={localPromoter.greetingDialogDisplayMobile}
          onChange={k =>
            armedLandmine(
              landmineNames[
                'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__EDIT-CUSTOMER-CHAT__MOBILE-GREETING-CHANGE'
              ],
              () => updatePromoterFields({ greetingDialogDisplayMobile: k.id }),
            )
          }
        />
      </div>
      <div>
        <TooltipHeading
          size={usedInSlideout ? 5 : 4}
          isBold={!usedInSlideout}
          tooltip={
            <>
              <strong>(Webchat only)</strong> Number of seconds of delay before
              the greeting is shown after the chat icon is loaded.
            </>
          }
          tooltipColor={usedInSlideout ? '#262626' : ''}
        >
          Greeting Tag
        </TooltipHeading>
      </div>
      <NumberInput
        className={classNames(
          'customer-chat-greeting-behaviour-editor__greeting-delay',
          {
            'customer-chat-greeting-behaviour-editor__greeting-delay-slideout-input':
              usedInSlideout,
          },
        )}
        min={0}
        value={localPromoter.greetingDialogDelay ?? 0}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          armedLandmine(
            landmineNames[
              'STARTBOTS-PROMOTERS__CUSTOMER-CHAT__EDIT-CUSTOMER-CHAT__WEBCHAT-GREETING-DELAY-CHANGE'
            ],
            () =>
              setLocalPromoter({
                ...localPromoter,
                greetingDialogDelay: Number(e.target.value || '0'),
              }),
          )
        }
        onBlur={() => updatePromoter(localPromoter)}
      />{' '}
      seconds
      {/* The following feature is currently hidden behind a feature flag because
          we can't figure out what to call it and how to properly describe the
          options.  One customer needs this urgently today, so we will revisit
          the text should we want to productize this fully.  */}
      {featureFlags.chat_widget_convo_continue_options && (
        <div>
          <div>
            <TooltipHeading
              addMarginAbove
              size={4}
              tooltip={
                <>
                  <strong>(Webchat only)</strong>
                </>
              }
              tooltipColor={usedInSlideout ? '#262626' : ''}
            >
              Advanced: Returning Visitors: Conversation Continuation Behaviour
            </TooltipHeading>
          </div>
          <div>
            This setting customizes the initialization behaviour of the Chat
            Widget when a visitor who has previously interacted with the chat
            widget returns to your site (or navigates between pages on your
            site).
          </div>
          Notes:
          <ul>
            <li>
              If it has been more than 24 hours since the visitor last
              interacted with the chat widget, we'll always show a greeting and
              start a new dialogue with the visitor. The new dialogue will be
              triggered from the active chat widget.
            </li>
            <li>
              If you're using any setting other than the first, you will also
              need to set this setting on all of the Chat Widgets on your site.
            </li>
          </ul>
          For visitors who previously interacted with this Chat Widget:
          <RadioInput
            className="customer-chat-conditions-editor__conversation-continue-behaviour-mode-radio"
            name="conversationContinueBehaviourMode"
            checked={
              localPromoter.conversationContinueBehaviour.mode === 'resume'
            }
            value="any"
            label="Continue the conversation started from this chat widget, no matter where they go on my site"
            onChange={() =>
              updatePromoterFields({
                conversationContinueBehaviour: {
                  ...localPromoter.conversationContinueBehaviour,
                  mode: 'resume',
                },
              })
            }
          />
          <RadioInput
            className="customer-chat-conditions-editor__conversation-continue-behaviour-mode-radio"
            name="conversationContinueBehaviourMode"
            checked={
              localPromoter.conversationContinueBehaviour.mode ===
              'active_search_token'
            }
            value="any"
            label="Continue the conversation while this chat widget remains the active widget"
            onChange={() =>
              updatePromoterFields({
                conversationContinueBehaviour: {
                  ...localPromoter.conversationContinueBehaviour,
                  mode: 'active_search_token',
                  searchToken: localPromoter.searchToken,
                },
              })
            }
          />
          <RadioInput
            className="customer-chat-conditions-editor__conversation-continue-behaviour-mode-radio"
            name="conversationContinueBehaviourMode"
            checked={
              localPromoter.conversationContinueBehaviour.mode ===
              'conditional_resume'
            }
            value="any"
            label="Continue the conversation started from this chat widget, but only if the following rule remains satisfied"
            onChange={() =>
              updatePromoterFields({
                conversationContinueBehaviour: {
                  ...localPromoter.conversationContinueBehaviour,
                  mode: 'conditional_resume',
                },
              })
            }
          />
          {localPromoter.conversationContinueBehaviour.mode ===
            'conditional_resume' && (
            <div className="customer-chat-conditions-editor__conversation-continue-behaviour-condition">
              Current path
              <SelectorWithArrow
                className="customer-chat-conditions-editor__conversation-continue-behaviour-operator"
                items={itemsFromObject(
                  PromoterConversationContinueBehaviourConditionOperatorCaptions,
                )}
                defaultLabel={
                  PromoterConversationContinueBehaviourConditionOperatorCaptions[
                    localPromoter.conversationContinueBehaviour?.condition
                      ?.operator || 'starts_with'
                  ]
                }
                onChange={(
                  operator: PromoterConversationContinueBehaviourConditionOperator,
                ) =>
                  updatePromoterFields({
                    conversationContinueBehaviour: {
                      ...localPromoter.conversationContinueBehaviour,
                      condition: {
                        ...(localPromoter.conversationContinueBehaviour
                          .condition || {
                          value: '',
                        }),
                        operator,
                      },
                    },
                  })
                }
              />
              <TextInput
                className="customer-chat-conditions-editor__rule-value"
                value={conversationContinueBehaviourConditionValue}
                onChange={e =>
                  setConversationContinueBehaviourConditionValue(e.target.value)
                }
                onBlur={() =>
                  updatePromoterFields({
                    conversationContinueBehaviour: {
                      ...localPromoter.conversationContinueBehaviour,
                      condition: {
                        ...(localPromoter.conversationContinueBehaviour
                          .condition || {
                          operator: 'starts_with',
                        }),
                        value: conversationContinueBehaviourConditionValue,
                      },
                    },
                  })
                }
              />
            </div>
          )}
          <RadioInput
            className="customer-chat-conditions-editor__conversation-continue-behaviour-mode-radio"
            name="conversationContinueBehaviourMode"
            checked={
              localPromoter.conversationContinueBehaviour.mode ===
              'always_greet'
            }
            value="any"
            label="Never continue the conversation started from this chat widget - a new conversation will be started from the active Chat Widget"
            onChange={() =>
              updatePromoterFields({
                conversationContinueBehaviour: {
                  ...localPromoter.conversationContinueBehaviour,
                  mode: 'always_greet',
                },
              })
            }
          />
        </div>
      )}
    </CustomerChatEditorAccordionItem>
  );
};
